import {
  Button,
  ButtonGroup,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { ChangeEvent, useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import HttpHelper from '../../utils/HttpHelper';
import useJobForm from '../../hooks/useJobForm';
import { useJobContext } from '../../JobContext';
import { handleDeleteClick } from '../../containers/ManageJobs/DeleteJob';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';

interface Column {
  id: 'title' | 'jobType' | 'published' | 'applicants' | 'deadline' | 'actions';
  label: string;
  minWidth?: number;
  align?: 'right' | 'center' | 'left';
  format?: (value: number) => string;
}

interface Job {
  id: number;
  title: string;
  jobType: string;
  published: boolean;
  applicationDeadline: string;
}

const columns: readonly Column[] = [
  {
    id: 'title',
    label: 'Title',
  },
  {
    id: 'jobType',
    label: 'Job Type',
    align: 'left',
  },
  {
    id: 'published',
    label: 'Published',
    align: 'center',
  },
  {
    id: 'applicants',
    label: 'Applicants',
    align: 'center',
  },
  {
    id: 'deadline',
    label: 'Application Deadline',
    align: 'center',
  },
  {
    id: 'actions',
    label: 'Actions',
    align: 'center',
  },
];

const ManageJobsTable: React.FC = () => {
  const [jobList, setJobList] = useState<Job[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const updateJobs = async () => {
    setLoading(true);
    const response = await HttpHelper.get('jobs/all');
    // Sort jobs: published (true) first, then unpublished (false)
    const sortedJobs = response.jobs.sort((a: Job, b: Job) => {
      if (a.published === b.published) return 0;
      return a.published ? -1 : 1;
    });
    setJobList(sortedJobs);
    setLoading(false);
  };

  useEffect(() => {
    updateJobs();
  }, []);

  const { jobTypeMapping } = useJobContext();

  const inverseJobType = Object.fromEntries(
    Object.entries(jobTypeMapping).map(([key, value]) => [value, key]),
  );

  const handleOpenDialog = (id: number) => {
    setSelectedJobId(id);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleConfirmDelete = async () => {
    if (selectedJobId) {
      await handleDeleteClick(selectedJobId);
      setJobList(prevJobList =>
        prevJobList.filter(job => job.id !== selectedJobId),
      );
    }
    setOpenDialog(false);
  };

  return (
    <Paper
      sx={{
        width: '100%',
        overflow: 'hidden',
        margin: 'auto',
        marginTop: '8px',
      }}
    >
      <TableContainer sx={{ maxHeight: '60vh' }}>
        <Table stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
              {columns.map(column => (
                <TableCell
                  key={column.id}
                  align={column.align}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <Typography variant="h5" sx={{ margin: '10px 0' }}>
                Loading...
              </Typography>
            ) : jobList.length === 0 ? (
              <Typography variant="h5" sx={{ margin: '10px 0' }}>
                No jobs found
              </Typography>
            ) : (
              jobList
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map(job => {
                  return (
                    <TableRow
                      hover
                      role="checkbox"
                      tabIndex={-1}
                      key={job.title}
                    >
                      <TableCell key="title" align={columns[0].align}>
                        {job.title}
                      </TableCell>
                      <TableCell key="jobType" align={columns[1].align}>
                        {inverseJobType[job.jobType]}
                      </TableCell>
                      <TableCell key="published" align={columns[2].align}>
                        {job.published ? 'Yes' : 'No'}
                      </TableCell>
                      <TableCell key="applicants" align={columns[3].align}>
                        <ButtonGroup>
                          <NavLink to={`/manage-jobs/applicants/${job.id}`}>
                            <Button>View Applicants</Button>
                          </NavLink>
                        </ButtonGroup>
                      </TableCell>
                      <TableCell key="deadline" align={columns[4].align}>
                        {job.applicationDeadline || '—'}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <ButtonGroup>
                          <NavLink to={`/manage-jobs/view/${job.id}`}>
                            <Button>View</Button>
                          </NavLink>
                          <NavLink to={`/manage-jobs/edit/${job.id}`}>
                            <Button>Edit</Button>
                          </NavLink>
                          <Button onClick={() => handleOpenDialog(job.id)}>
                            Delete
                          </Button>
                        </ButtonGroup>
                      </TableCell>
                    </TableRow>
                  );
                })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 100]}
        component="div"
        count={jobList.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{'Confirm Deletion'}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this job?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleConfirmDelete} autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default ManageJobsTable;
